import { useMemo } from "react";

import { parseGid, useCart } from "@shopify/hydrogen-react";
import {
  type ProductVariant,
  type SelectedOption,
} from "@shopify/hydrogen-react/storefront-api-types";
import useSWR from "swr/immutable";

import getFallbackCartUpsellsQuery from "@graphql/get-fallback-cart-upsells.graphql";
import getProductRecommendationsQuery from "@graphql/get-product-recommendations.graphql";
import getProductVariantsByIdsQuery from "@graphql/get-product-variants-by-ids.graphql";
import {
  dataAPIFetcher,
  multiStorefrontAPIFetcher,
  storefrontAPIFetcher,
} from "@react/hooks/fetchers";
import { useProductBundles } from "@react/hooks/useProductBundles";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import { selectedOrFirstAvailableVariant } from "@react/utilities";
import {
  OUR_PLACE_DATA_API_URL,
  STOREFRONT_API_URL,
  STOREFRONT_CART_ATTRIBUTES,
} from "@tablescape/constants";
import {
  type CartProductConnection,
  type CartSectionSettings,
  type CartUpsellProductMetadata,
} from "@tablescape/state";
import {
  formatProduct,
  getShopifyGlobalId,
  hasCartLineAttribute,
  isProductVariantAvailableForSale,
  mapIntelligemsPriceToVariant,
} from "@tablescape/utilities";

type FallbackAPIConnection = {
  data: {
    collection: {
      products: {
        nodes: CartProductConnection[];
      };
    };
  };
};

type ProductRecommendationsAPIConnection = {
  data: {
    productRecommendations: CartProductConnection[];
  };
};

type ProductVariantsAPIConnection = {
  data: {
    nodes: (Pick<ProductVariant, "id" | "selectedOptions"> & {
      product: CartProductConnection;
    })[];
  };
};

const formatUpsellProductMetadata = (
  node: CartProductConnection,
  selectedOptions?: SelectedOption[]
) => {
  const product = formatProduct(node);
  const selectedVariant = selectedOptions
    ? selectedOrFirstAvailableVariant(product, selectedOptions)
    : product.variants[0];

  return {
    id: product.id,
    product,
    selectedOptions: selectedOptions ?? product.variants[0].selectedOptions,
    selectedVariant: mapIntelligemsPriceToVariant(selectedVariant),
  };
};

export function useCartUpsells({
  limit = 5,
  upsellDataApiEnabled,
}: {
  limit: number;
  upsellDataApiEnabled: boolean;
}) {
  const {
    meta: {
      localization: { country },
    },
  } = useSectionSettings<CartSectionSettings>();
  const { productBundles } = useProductBundles();
  const { id: unparsedCartId, lines: cartLines } = useCart();
  const cartId = useMemo(() => parseGid(unparsedCartId).id, [unparsedCartId]);
  const productIds = useMemo(
    () =>
      cartLines?.map((cartLine) => {
        return getShopifyGlobalId(
          "Product",
          parseGid(cartLine?.merchandise?.product?.id).id ?? "0"
        );
      }),
    [cartLines]
  );
  const bundleEligibleCartLines = useMemo(
    () =>
      cartLines?.filter(
        (cartLine) =>
          !hasCartLineAttribute(cartLine, STOREFRONT_CART_ATTRIBUTES.BUNDLES) &&
          !hasCartLineAttribute(cartLine, STOREFRONT_CART_ATTRIBUTES.UPSELL)
      ),
    [cartLines]
  );
  const excludeProductIds = useMemo(
    () =>
      cartLines
        ?.map((line) => line?.merchandise?.product?.id)
        .filter((id): id is string => !!id),
    [cartLines]
  );
  const variantIds = useMemo(() => {
    return cartLines?.map((cartLine) => {
      return parseFloat(parseGid(cartLine?.merchandise?.id).id ?? "0");
    });
  }, [cartLines]);

  const { data: dataUpsells, isLoading: isDataUpsellsLoading } = useSWR(
    upsellDataApiEnabled && variantIds
      ? [OUR_PLACE_DATA_API_URL, cartId, variantIds]
      : null,
    dataAPIFetcher
  );

  const { data: fallbackCartUpsells, isLoading: isFallbackCartUpsellsLoading } =
    useSWR<FallbackAPIConnection>(
      [
        STOREFRONT_API_URL,
        getFallbackCartUpsellsQuery,
        { handle: "upsell-fallback-collection", country },
      ],
      storefrontAPIFetcher
    );

  const {
    data: multiProductRecommendations,
    isLoading: isMultiProductRecommendationsLoading,
  } = useSWR<ProductRecommendationsAPIConnection[]>(
    productIds
      ? productIds.map((productId) => [
          STOREFRONT_API_URL,
          getProductRecommendationsQuery,
          { productId, intent: "COMPLEMENTARY", country },
        ])
      : null,
    multiStorefrontAPIFetcher
  );

  const {
    data: storefrontCartUpsells,
    isLoading: isStorefrontCartUpsellsLoading,
  } = useSWR<ProductVariantsAPIConnection>(() => {
    return dataUpsells?.data?.cartUpsells?.length
      ? [
          STOREFRONT_API_URL,
          getProductVariantsByIdsQuery,
          {
            variantIds: dataUpsells.data.cartUpsells.map((u) =>
              getShopifyGlobalId("ProductVariant", u.variant_id)
            ),
            country,
          },
        ]
      : null;
  }, storefrontAPIFetcher);

  const isLoading = useMemo(() => {
    return (
      isDataUpsellsLoading ||
      isFallbackCartUpsellsLoading ||
      isMultiProductRecommendationsLoading ||
      isStorefrontCartUpsellsLoading
    );
  }, [
    isDataUpsellsLoading,
    isFallbackCartUpsellsLoading,
    isMultiProductRecommendationsLoading,
    isStorefrontCartUpsellsLoading,
  ]);

  const matchingProductBundles = useMemo(() => {
    if (isLoading) {
      return [];
    }

    return (
      productBundles
        ?.filter(({ discount, products }) => {
          if (!discount) {
            return false;
          }

          const bundleEligibleProductIds = bundleEligibleCartLines.map(
            (cartLine) => {
              return getShopifyGlobalId(
                "Product",
                parseGid(cartLine?.merchandise?.product?.id).id ?? "0"
              );
            }
          );

          return !!products.find((product) =>
            bundleEligibleProductIds?.includes(product.id)
          );
        })
        ?.map(({ products }) => products) ?? []
    );
  }, [bundleEligibleCartLines, isLoading, productBundles]);

  const productRecommendations = useMemo<
    CartUpsellProductMetadata[] | undefined
  >(() => {
    if (isLoading) {
      return undefined;
    }

    return multiProductRecommendations
      ?.reduce<CartProductConnection[]>(
        (acc, data) => [...acc, ...(data?.data?.productRecommendations ?? [])],
        []
      )
      .map((node) => formatUpsellProductMetadata(node));
  }, [isLoading, multiProductRecommendations]);

  const derivedUpsellProducts = useMemo<
    CartUpsellProductMetadata[] | undefined
  >(() => {
    if (isLoading) {
      return undefined;
    }

    if (storefrontCartUpsells?.data?.nodes?.length) {
      return storefrontCartUpsells.data.nodes
        .filter((upsell) => !!upsell)
        .filter(
          ({ product }) =>
            process.env.STOREFRONT_ENV === "staging" || product.onlineStoreUrl
        )
        .map(({ product, selectedOptions }) =>
          formatUpsellProductMetadata(product, selectedOptions)
        );
    }

    if (fallbackCartUpsells?.data?.collection?.products?.nodes) {
      return fallbackCartUpsells.data.collection.products.nodes.map((node) => {
        return formatUpsellProductMetadata(node);
      });
    }

    return undefined;
  }, [
    fallbackCartUpsells?.data?.collection?.products,
    isLoading,
    storefrontCartUpsells?.data?.nodes,
  ]);

  const derivedRecommendedProducts = useMemo(() => {
    if (!productRecommendations || !derivedUpsellProducts) {
      return undefined;
    }

    return [...productRecommendations, ...derivedUpsellProducts]
      .reduce<CartUpsellProductMetadata[]>((acc, current) => {
        if (acc.some((meta) => meta.id === current.id)) {
          return acc;
        }

        const potentialBundleMatch = matchingProductBundles.find((products) =>
          products.some((product) => product.id === current.id)
        );

        const upsellSelfInBundleMatch = potentialBundleMatch?.find(
          (product) => product.id === current.id
        );

        if (upsellSelfInBundleMatch) {
          const otherBundleComponentsInCart = potentialBundleMatch?.filter(
            (product) => product.id !== current.id
          );

          const relatedCartLines = otherBundleComponentsInCart?.map((product) =>
            bundleEligibleCartLines.find(
              (line) => line?.merchandise?.product?.id === product.id
            )
          );

          const selectedVariant =
            relatedCartLines && relatedCartLines[0]
              ? selectedOrFirstAvailableVariant(
                  upsellSelfInBundleMatch,
                  relatedCartLines[0].merchandise?.selectedOptions
                )
              : upsellSelfInBundleMatch.variants[0];

          acc.unshift({
            id: upsellSelfInBundleMatch.id,
            product: upsellSelfInBundleMatch,
            selectedOptions: selectedVariant.selectedOptions,
            selectedVariant,
            ...(relatedCartLines ? { relatedCartLines } : {}),
          });
        } else {
          acc.push(current);
        }

        return acc;
      }, [])
      .filter(({ selectedVariant }) =>
        isProductVariantAvailableForSale(selectedVariant)
      )
      .filter((meta) => !excludeProductIds?.includes(meta.id))
      .slice(0, limit);
  }, [
    bundleEligibleCartLines,
    derivedUpsellProducts,
    excludeProductIds,
    limit,
    matchingProductBundles,
    productRecommendations,
  ]);

  return {
    dataDrivenUpsells: !!dataUpsells?.data?.cartUpsells?.length,
    upsellProducts: derivedRecommendedProducts,
  };
}
